import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  AccountCircleRounded,
  HomeRounded,
  GridViewRounded,
  FlagRounded,
  Logout,
  QuestionAnswer,
  Phone
} from "@mui/icons-material";
import { logout } from "../auth";
import useCurrentUserData from "../hooks/useCurrentUserData";
import { dashboardPalette } from "../theme";
import profileIcon from "../assets/profile.png";
import RewardedProfileDisplay from "./RewardedProfileDisplay";
import { useAuth } from "../contexts/AuthContext";

const MENU_ITEMS = [
  {
    id: "dashboard",
    label: "Inicio",
    icon: HomeRounded,
    path: "/dashboard",
  },
  {
    id: "redeem",
    label: "Progreso y Recompensas",
    icon: FlagRounded,
    path: "/redeem",
  },
  {
    id: "skills",
    label: "Skills",
    icon: GridViewRounded,
    path: "/skills",
  },
  {
    id: "profile",
    label: "Perfil",
    icon: AccountCircleRounded,
    path: "/profile",
  },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useCurrentUserData();
  const sidebarAvatarSrc =
    userData && userData.PortraitImg !== undefined
      ? userData.PortraitImg || profileIcon
      : profileIcon;

  const [equippedImage, setEquippedImage] = useState(String);
  const { user } = useAuth();

  const raw = localStorage.getItem(user!.uid)
  const parsed = JSON.parse(raw!);

  useEffect(() => {
    if (user) {
      setEquippedImage(getCurrentImageReward())
    }
  }, [getCurrentImageReward()])
  //funcion improvisada para inicializar la Imagen recompensa actual
  function getCurrentImageReward() {
    for (let i = 0; parsed?.Recompensas?.length > i; i++) {
      if (parsed.Recompensas[i]?.Equipped === true) {
        return parsed.Recompensas[i]?.Image
      }
    }
  }

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem(user?.uid!)
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <Box
      sx={{
        width: 86,
        minHeight: "100vh",
        height: "auto",
        alignSelf: "stretch",
        background: "var(--dashboard-sidebar)",
        borderRight: `2px solid ${dashboardPalette.panelBorder}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 2,
        }}
      >
        <RewardedProfileDisplay
          src={sidebarAvatarSrc}
          alt="Perfil"
          fallbackSrc={profileIcon}
          width={56}
          height={56}
          border={`3px solid ${dashboardPalette.headerBlue}`}
          boxShadow="0 3px 0 rgba(47,43,116,0.25)"
          backgroundColor="#fff4c4"
          rewards="{[
            ...(userData?.Recompensas ?? []),
            ...(userData?.Items ?? []),
          ]}"
          badgeSize={20}
          overlaySize={24}
        />
        {equippedImage ?
        <Box
          sx={{
            position: "absolute"
          }}>
          <Box
            component="img"
            src={equippedImage}
            sx={{
              width: 56,
              height: 56,
              position: "relative",
              top: -6,
            }}
          />
        </Box> : <></>}
      </Box>

      <Box sx={{ width: "100%", flex: 1, pt: 4 }}>
        <List sx={{ p: 0 }}>
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <ListItem
                key={item.id}
                sx={{ px: 0, py: 0.4, justifyContent: "center" }}
              >
                <Tooltip title={item.label} placement="right">
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      minWidth: 70,
                      maxWidth: 70,
                      height: 64,
                      borderRadius: 0,
                      justifyContent: "center",
                      position: "relative",
                      bgcolor: isActive
                        ? "var(--dashboard-yellow)"
                        : "transparent",
                      borderTop: isActive
                        ? `1px solid ${dashboardPalette.accentYellowDark}`
                        : "none",
                      borderBottom: isActive
                        ? `1px solid ${dashboardPalette.accentYellowDark}`
                        : "none",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        bgcolor: isActive
                          ? dashboardPalette.accentYellowDark
                          : "transparent",
                      },
                      "&:hover": {
                        bgcolor: isActive
                          ? "var(--dashboard-yellow)"
                          : "#f7f7f7",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        color: "var(--dashboard-header-blue)",
                      }}
                    >
                      <Icon sx={{ fontSize: 28 }} />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}

          <ListItem
            sx={{ px: 0, py: 0.4, justifyContent: "center" }}
          >
            <Link to="https://forms.gle/cRTYz5VSeBDx2asGA">
              <Tooltip title={"Contacto"} placement="right">

                <ListItemButton
                  sx={{
                    minWidth: 70,
                    maxWidth: 70,
                    height: 64,
                    borderRadius: 0,
                    justifyContent: "center",
                    position: "relative",
                    bgcolor: "transparent",
                    borderTop: "none",
                    borderBottom: "none",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      bgcolor: "transparent",
                    },
                    "&:hover": {
                      bgcolor: "#f7f7f7",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      color: "var(--dashboard-header-blue)",
                    }}
                  >
                    <Phone />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </Link>
          </ListItem>
          {/** 
          <ListItem
            sx={{ px: 0, py: 0.4, justifyContent: "center" }}
          >
            <Link to={-->"Insertar Link Aqui (String)"<--}>
              <Tooltip title={"Cuestionario"} placement="right">

                <ListItemButton
                  sx={{
                    minWidth: 70,
                    maxWidth: 70,
                    height: 64,
                    borderRadius: 0,
                    justifyContent: "center",
                    position: "relative",
                    bgcolor: "transparent",
                    borderTop: "none",
                    borderBottom: "none",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      bgcolor: "transparent",
                    },
                    "&:hover": {
                      bgcolor: "#f7f7f7",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      color: "var(--dashboard-header-blue)",
                    }}
                  >
                    <QuestionAnswer />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </Link>
          </ListItem>
          */}
        </List>




      </Box>

      <Box sx={{ pb: 2 }}>
        <Tooltip title="Cerrar sesión" placement="right">
          <IconButton
            onClick={handleLogout}
            sx={{
              color: "var(--dashboard-header-blue)",
              border: `2px solid ${dashboardPalette.panelBorder}`,
              bgcolor: "#fff",
              "&:hover": {
                bgcolor: "#fff4c4",
              },
            }}
          >
            <Logout />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default Sidebar;
